import {useEffect, useState} from "react";
import {getConfig} from "../../../_configs/AppConfig";
import ClickableLink from "../../../components/utilities/ClickableLink";
import {PermissionGroupSection} from "./PermissionGroupSection";
import NotificationManager from "../../../components/notifications/NotificationManager";
import WatsonApi from "../../../backends/WatsonApi";
import SchoolBlocksLoadingIndicator from "../../../components/utilities/SchoolBlocksLoadingIndicator";
import useSWR from "swr";
import styles from "./AuthenticationTab.module.scss";
import LoginButton from "../../../components/admin/sidebar/items/login/LoginButton";
import FontAwesome from "../../../components/utilities/FontAwesome";
import ImageComponent from "../../../components/utilities/ImageComponent";
import {useStores} from "../../../stores/useStores";

const {publicRuntimeConfig} = getConfig();

export interface GroupOrOUList {
    [OUNameOrGroupId: string]: string,
}

export interface IGroupMappings {
    [orgId: string]: {
        adminGroup: string | string[],
        staffGroup: string | string[],
        teacherGroup?: string | string[],
        studentGroup?: string | string[],
        department?: string, // office365 only
        companyName?: string, // office365 only
    }
}

export function GoogleAuthenticationTab() {
    const {organizationStore} = useStores();
    const identityProvider: string = organizationStore.organization.json_data.settings.identity.provider;
    const {
        adminAccount,
        searchFolderId,
        domain,
        searchDrive,
        userFieldsToOverrideAndSync,
    } = organizationStore.organization.json_data.settings.identity.google;

    const [hasChanged, setHasChanged] = useState(false);
    const [authenticationDomain, setAuthenticationDomain] = useState<string>(domain);
    const [shouldSearchDrive, setShouldSearchDrive] = useState<boolean>(searchDrive);
    const [syncTitle, setSyncTitle] = useState(userFieldsToOverrideAndSync.title);
    const [syncPhone, setSyncPhone] = useState(userFieldsToOverrideAndSync.phone);
    const [syncEmail, setSyncEmail] = useState(userFieldsToOverrideAndSync.email);
    const [topLevelSearchFolderId, setTopLevelSearchFolderId] = useState<string>(searchFolderId);
    const [googleClassroomIntegrationEmail, setGoogleClassroomIntegrationEmail] = useState(false);

    const initialGroupMappings = organizationStore?.organization?.json_data?.settings?.identity?.google?.groupMappings;
    const [groupMappings, setGroupMappings] = useState(initialGroupMappings);
    const {data, isLoading} = useSWR("fetch-group-mappings-google", async () => {
        try {
            const client = await WatsonApi();
            const response = await client.apis.settings.settings_directory_group_mappings({
                id: organizationStore.currentOrganization.id,
                selected_provider: "google",
            });
            return JSON.parse(response.data);
        } catch (e) {
            console.error(e);
            return {
                message: "Error fetching auth group data.",
            }
        }
    }, {
        revalidateOnMount: true,
        revalidateIfStale: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
    });
    useEffect(() => {
        if (!isLoading) {
            setGroupMappings(() => {
                const value = {};
                [data.district, ...data.schools].forEach(org => {
                    const obj = {
                        adminGroup: initialGroupMappings?.[org.id]?.adminGroup || [],
                        staffGroup: initialGroupMappings?.[org.id]?.staffGroup || [],
                        teacherGroup: initialGroupMappings?.[org.id]?.teacherGroup || [],
                        studentGroup: initialGroupMappings?.[org.id]?.studentGroup || [],
                    }

                    if (org.type !== "district") {
                        obj.teacherGroup = initialGroupMappings?.[org.id]?.teacherGroup || [];
                        obj.studentGroup = initialGroupMappings?.[org.id]?.studentGroup || [];
                    }

                    value[org.id] = obj;
                });
                return value;
            });
            setGoogleClassroomIntegrationEmail(data.googleClassroomIntegrationEmail);
        }
    }, [isLoading])

    async function handleSubmit(e) {
        e.preventDefault();
        try {
            const client = await WatsonApi();
            const response = await client.apis.settings.settings_update({
                "id": organizationStore.currentOrganization.id,
                "data": {
                    "identity": {
                        "provider": "google",
                        "google": {
                            "domain": authenticationDomain,
                            "searchDrive": shouldSearchDrive,
                            "searchFolderId": topLevelSearchFolderId,
                            "userFieldsToOverrideAndSync": {
                                "title": syncTitle,
                                "phone": syncPhone,
                                "email": syncEmail,
                            },
                            "groupMappings": groupMappings,
                        },
                    },
                },
            });
            const settings = response.body.settings;
            if (settings) {
                organizationStore.organization.json_data.settings = settings;
                NotificationManager.success("Settings successfully updated!");
            }
        } catch (e) {
            switch (e.statusCode) {
                case 400:
                    NotificationManager.error("Bad Request: Settings not updated.");
                    break;
                case 404:
                    NotificationManager.error("Not Found: Organization settings not found.");
                    break;
                case 500:
                    NotificationManager.error("Internal Server Error: Settings not updated.");
                    break;
                default:
                    NotificationManager.error("Unknown Error: Settings not updated.");
                    break;
            }
        }
    }

    return (isLoading || !data) ? (<div style={{textAlign: "center", marginTop: "6em"}}>
        <SchoolBlocksLoadingIndicator fill={"$dark-gray"}/>
    </div>) : (<div>
        <div className="sb-settings-block-content-heading">
            <h4>Allow your users to authenticate with Google Apps for Education.</h4>
        </div>
        <div>
            <p>To allow your Students, Teachers, and Staff to authenticate with Google Apps for
                Education, your Domain Administrator must delegate some 'read only' privileges to
                School Blocks. Please follow the directions <ClickableLink
                    href="https://developers.google.com/admin-sdk/directory/v1/guides/delegation#delegate_domain-wide_authority_to_your_service_account"
                ><u><b>here</b></u></ClickableLink> to allow
                School Blocks to authenticate your users.</p>
            <p>The School Blocks Client Name that you need to enter in Google's Admin Console is:</p>
            <pre>{publicRuntimeConfig.google.serviceAccount.clientId}</pre>
            <p>The scopes that must be authorized are:</p>
            <pre>
                {(!isLoading && data && data?.scopes) && data?.scopes.map(scope => scope + ",\n")}
            </pre>
        </div>
        <form id="district-update-form" onSubmit={handleSubmit} onChange={() => setHasChanged(true)}
        >
            <div>
                <label>Authentication Domain:</label>
                <input title="Google authentication domain" width="50"
                       type="text"
                       placeholder="Authentication Domain"
                       value={authenticationDomain}
                       onChange={e => setAuthenticationDomain(e.target.value)}
                />
            </div>
            <div>
                <ul>
                    <li>
                        If Google Apps for Education is properly configured based on the above information,
                        then you should see drop down lists below listing all organizational units and groups in
                        your Google organization. If it is not properly configured, then please try logging in to
                        School Blocks with your Google Apps for Education account.
                    </li>
                    {identityProvider === "google" ?
                        <li key={"sync"}>We are syncing your staff directory and permissions with Google.</li> :
                        <li key={"nosync"}>
                            We are <strong>not</strong> syncing your staff directory and permissions with
                            Google at this time. Please contact support if you would like to change this.
                        </li>}
                    <li>
                        The connection with google was setup with the following Google "Super Admin"
                        account: <strong>{adminAccount}</strong>.
                        If this account is no longer a "Super Admin" account in Google, please contact
                        support to change this or else users will not be able to authenticate.
                    </li>
                </ul>
            </div>
            <div>
            </div>
            <div>
                <label>Search Google Drive:</label>
            </div>
            <div>
                <input
                    type={"checkbox"}
                    checked={shouldSearchDrive}
                    onChange={() => setShouldSearchDrive(v => !v)}
                    title={"Google Drive search settings"}
                    style={{marginLeft: "1em", width: "0.8em"}}
                />
                Include Google Drive files in the site's search results. All publicly
                accessible files across the entire organization's drive will be searched.
            </div>
            <div style={{marginLeft: "1em"}}>
                <p>
                    Limit the files included in the search to a single, top-level folder ID:
                    <br/>
                    <input width="50"
                           type="text" placeholder="Search Folder ID" value={topLevelSearchFolderId}
                           onChange={e => setTopLevelSearchFolderId(e.target.value)}/>
                </p>
                <p>
                    Find your folder ID by navigating to Google Drive (in another browser window)
                    then viewing the top-level folder that contains files/folders that should be
                    searched. At the end of the URL, after the word 'folders/',
                    there is a long string of numbers and characters. That is the folder ID!
                </p>
                <p>
                    Because of restrictions in how Google Drive searching works, new folders
                    underneath the above folder ID won't be included in the search results until about
                    an hour after they have been created.
                </p>
                <p style={{fontWeight: 600}}>
                    Important! Google Drive permissions propagate downward and are inherited by all child files and
                    folders. The top level folder must be at root level or not be below a folder that the configured
                    "Super Admin" account cannot read.
                </p>
            </div>
            <div>
                <label>Staff Directories:</label>
                <p>
                    Users listed in the groups or OUs identified below will be synced to SchoolBlocks
                    so that they show up in Staff Directory pages and blocks. Users are able to edit
                    their displayed information such as first name, last name, email, etc. If you
                    would like to override any changes made by the user and sync specific fields,
                    check the boxes below. Not all fields are available to overwrite and sync.
                </p>
            </div>
            <div>
                Fields to sync from the Google user object for each staff member:<br/>
                <div style={{display: "inline-block"}} data-content={`When enabled, staff members are unable to edit their own titles in SchoolBlocks. Titles
                        must be updated in Google Directory.`} className={styles.tooltip}>
                    <input
                        id={"UpdateForm[identity][google][userFieldsToOverrideAndSync][title]"}
                        type={"checkbox"}
                        checked={!!syncTitle}
                        onChange={() => setSyncTitle(v => !v)}
                        style={{marginLeft: "1em", width: "0.8em"}}
                        title={"Sync a staff member's title"}
                    /> <label htmlFor={"UpdateForm[identity][google][userFieldsToOverrideAndSync][title]"}>Title</label>
                </div>
                <div>
                    <input
                        title={"Sync staff member's phone"}
                        id={"UpdateForm[identity][google][userFieldsToOverrideAndSync][phone]"}
                        type={"checkbox"}
                        checked={!!syncPhone}
                        onChange={() => setSyncPhone(v => !v)}
                        style={{marginLeft: "1em", width: "0.8em"}}
                    /> <label htmlFor={"UpdateForm[identity][google][userFieldsToOverrideAndSync][phone]"}>Phone</label>
                </div>
                <div>
                    <input
                        title={"Sync staff member's email"}
                        id={"UpdateForm[identity][google][userFieldsToOverrideAndSync][email]"}
                        type={"checkbox"}
                        checked={!!syncEmail}
                        onChange={() => setSyncEmail(v => !v)}
                        style={{marginLeft: "1em", width: "0.8em"}}
                    /> <label htmlFor={"UpdateForm[identity][google][userFieldsToOverrideAndSync][email]"}>Email</label>
                </div>
            </div>
            <div>
                <table className="table table-striped">
                    <thead>
                    <tr>
                        <th style={{textAlign: "center"}}>Organization</th>
                        <th style={{textAlign: "center"}}>Permission Groups</th>
                    </tr>
                    </thead>
                    <tbody>
                    {(!isLoading && data?.district) && <PermissionGroupSection
                        organization={data.district}
                        useGroupSearch={data.useGroupSearch}
                        identityOUList={data.identityOuList}
                        identityGroupsList={data.identityGroupsList}
                        groupOuReference={data.groupOuReference}
                        key={data.district.id}
                        provider={"google"}
                        groupMappings={groupMappings}
                        setGroupMappings={setGroupMappings}
                        setHasChanged={setHasChanged}
                    />}
                    {!!(!isLoading && data?.schools?.length) && data.schools.map(school => {
                        return <PermissionGroupSection
                            key={school.id}
                            organization={school}
                            useGroupSearch={data.useGroupSearch}
                            identityOUList={data.identityOuList}
                            identityGroupsList={data.identityGroupsList}
                            groupOuReference={data.groupOuReference}
                            provider={"google"}
                            groupMappings={groupMappings}
                            setGroupMappings={setGroupMappings}
                            setHasChanged={setHasChanged}
                        />
                    })}
                    </tbody>
                </table>
            </div>
            <div className="sb-settings-block-footer">
                <button type="submit" className="btn sb-organization-color-element-bg" name="submit"
                        disabled={!hasChanged}>Save
                </button>
            </div>
        </form>
    </div>);
}
